import { useEffect, useRef, useState } from 'react';
import { fetchApi } from '../config/api';
import { notifyDogsChanged } from '../utils/dogEvents';

interface DogPhotoTaskStatus {
  galleryPhotoId: number | null;
  status: string;
}

const POLL_INTERVAL_MS = 4000;

const unwrap = (res: any) => res?.data ?? res;

/**
 * Pollt, welche ComfyUI-Foto-Aufgaben (Freistellen) fuer diesen Hund gerade noch laufen (Portrait
 * und/oder einzelne Galerie-Fotos), damit die UI waehrend der Bearbeitung ein Overlay auf dem
 * jeweils betroffenen Foto zeigen kann - ohne Neuladen der Seite. onSettled wird pro Foto genau
 * einmal aufgerufen, sobald es aus der "noch laufend"-Liste verschwindet (Aufruf mit
 * galleryPhotoId=null fuer das Portrait), damit der Aufrufer die neue Foto-URL nachladen kann.
 */
export function useDogPhotoTasks(dogId: string | undefined, onSettled?: (galleryPhotoId: number | null) => void) {
  const [tasks, setTasks] = useState<DogPhotoTaskStatus[]>([]);
  const prevKeysRef = useRef<Set<string>>(new Set());
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (!dogId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetchApi(`/dogs/${dogId}/photo-tasks`);
        if (cancelled) return;
        const next: DogPhotoTaskStatus[] = unwrap(res) || [];
        const nextKeys = new Set(next.map((t) => String(t.galleryPhotoId ?? 'portrait')));
        const settledKeys = [...prevKeysRef.current].filter((key) => !nextKeys.has(key));
        prevKeysRef.current = nextKeys;
        setTasks(next);
        settledKeys.forEach((key) => onSettledRef.current?.(key === 'portrait' ? null : Number(key)));
        // Ein fertig freigestelltes Portrait hat eine neue URL - auch fuer die Kurzwahl in der Sidebar.
        if (settledKeys.includes('portrait')) notifyDogsChanged();
      } catch {
        // Polling ist best-effort - ein einzelner fehlgeschlagener Versuch wird beim naechsten Intervall wiederholt.
      }
    };

    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [dogId]);

  return {
    isPortraitPending: tasks.some((t) => t.galleryPhotoId == null),
    isGalleryPhotoPending: (photoId: number) => tasks.some((t) => t.galleryPhotoId === photoId)
  };
}
